import React, { useState } from "react";
import "./login.css";
import { Link, useNavigate } from "react-router-dom";
import login_img from "../../assets/image/user_signup.jpeg";
import { api } from "../../api/api";
import { useForm, Controller } from "react-hook-form";
import CountryCode from "../../Components/common/CountryCode/CountryCode";
import {
  OtpPanel,
  emptyOtp,
  getApiMessage,
  isApiSuccess,
  prettyPhone,
  useCountdown,
} from "../../Components/common/OtpInput/OtpInput";

const OTP_VALIDITY = 10 * 60; // backend keeps verification OTPs for 10 minutes
const RESEND_COOLDOWN = 30;

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [expiresIn, setExpiresIn] = useCountdown(0);
  const [resendIn, setResendIn] = useCountdown(0);
  const [otp, setOtp] = useState(emptyOtp());
  const [otpShow, setOtpShow] = useState(false);
  const [error, setError] = useState("");            // OTP error
  const [formError, setFormError] = useState("");    // Register/API error
  const [passwordShow, setPasswordShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      countryPhone: { countryCode: "+91", phone: "" }, // 👈 phone lives here
      password: "",
      confirmPassword: "",
    },
    mode: "onSubmit",
  });

  // form rules
  const password = watch("password");

  const registerOptions = {
    name: { required: "Name is required" },
    email: {
      // Email is optional — validate format only if filled
      validate: (value) => {
        if (!value || !String(value).trim()) return true;
        const ok = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          String(value).trim()
        );
        return ok || "Enter a valid email address";
      },
    },
    password: { required: "Password is required" },
    confirmPassword: {
      required: "Confirm password is required",
      validate: (value) => value === password || "Passwords do not match",
    },
  };

  // REGISTER
  const handleRegister = async (data) => {
    setFormError("");
    try {
      setSubmitting(true);

      const code = (data?.countryPhone?.countryCode || "").trim();
      const digits = String(data?.countryPhone?.phone || "").replace(/\D/g, "");
      const emailValue = String(data?.email || "").trim();

      if (!code) throw new Error("Please select a valid country code.");
      if (!digits) throw new Error("Phone number is required.");
      if (digits.length !== 10) {
        throw new Error("Please enter a valid 10-digit phone number.");
      }

      // Phone + country code together, e.g. +918058401211
      const fullPhone = code.startsWith("+")
        ? `${code}${digits}`
        : `+${code}${digits}`;

      const payload = {
        name: data.name,
        password: data.password,
        phone: fullPhone,
      };
      if (emailValue) payload.email = emailValue;

      const res = await api(
        `api/v1/student/register`,
        payload,
        "postWithoutToken",
        "",
        ""
      );

      const isFailed =
        res?.status === false ||
        res?.success === false ||
        (Array.isArray(res?.errors) && res.errors.length > 0);

      const success =
        !isFailed &&
        (res?.success === true ||
          res?.status === true ||
          String(res?.status || "").toLowerCase() === "success");

      if (!success) {
        const msg =
          res?.errors?.[0]?.msg ||
          res?.errors?.[0]?.message ||
          res?.response ||
          res?.message ||
          res?.error ||
          "Registration failed";
        throw new Error(msg);
      }

      setOtpShow(true);
      setEmail(emailValue);
      setPhone(fullPhone);
      setOtp(emptyOtp());
      setError("");
      setExpiresIn(OTP_VALIDITY);
      setResendIn(RESEND_COOLDOWN);
    } catch (e) {
      setFormError(e?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // VERIFY OTP
  const handleVerify = async () => {
    setError("");
    const code = otp.join("");
    if (code.length !== otp.length) {
      setError("Please enter the complete 6-digit code.");
      return;
    }
    if (expiresIn <= 0) {
      setError("This code has expired. Please request a new one.");
      return;
    }
    try {
      setVerifying(true);
      // phone is always present and unique, so verify by phone
      const res = await api(
        `api/v1/student/verifyEmail`,
        { phone, otp: code },
        "postWithoutToken"
      );
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "OTP verification failed"));
      navigate("/login");
    } catch (e) {
      setError(e?.message || "Something went wrong");
    } finally {
      setVerifying(false);
    }
  };

  // RESEND OTP
  const handleResendOTP = async () => {
    setError("");
    try {
      setResending(true);
      const res = await api(`api/v1/student/resendOTP`, { phone }, "postWithoutToken");
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "Failed to resend OTP"));
      setOtp(emptyOtp());
      setExpiresIn(OTP_VALIDITY);
      setResendIn(RESEND_COOLDOWN);
    } catch (e) {
      setError(e?.message || "Something went wrong while resending OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="register-area register overflow-hidden">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 mx-auto">
              <h3 className="text-center mb-4">
                Register to join us
                <span> Welcome ! Please Enter Your Details.</span>
              </h3>
              <div className="img_sec img_showcase">
                <img src={login_img} alt="login" />
                <span className="img_chip chip_top" aria-hidden="true">
                  <i className="fas fa-user-plus" /> Join Classhila
                </span>
                <span className="img_chip chip_bottom" aria-hidden="true">
                  <i className="fas fa-book-open" /> Start learning
                </span>
              </div>
              <div className="contact-form">
                {otpShow ? (
                  <form
                    className="mt-4"
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleVerify();
                    }}
                  >
                    <h4 className="otp-heading text-center">Verify your phone number</h4>
                    <OtpPanel
                      target={prettyPhone(phone)}
                      otp={otp}
                      setOtp={setOtp}
                      error={error}
                      expiresIn={expiresIn}
                      resendIn={resendIn}
                      onResend={handleResendOTP}
                      resending={resending}
                    />
                    {email && (
                      <p className="otp-note text-center">
                        <i className="fas fa-envelope me-1" /> We also sent it to {email}
                      </p>
                    )}

                    <button
                      className="btn_secondary btn_md w-100 mt-4 text-center"
                      type="submit"
                      disabled={verifying}
                    >
                      {verifying && (
                        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      )}
                      {verifying ? "Verifying..." : "Verify & Continue"}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit(handleRegister)} className="mt-4" noValidate>
                    <div className="mb-3">
                      <input
                        {...register("name", registerOptions.name)}
                        type="text"
                        className="form-control"
                        placeholder="*Full Name"
                      />
                      {errors.name && (
                        <span className="text-danger small">
                          {errors.name.message}
                        </span>
                      )}
                    </div>

                    <div className="mb-3">
                      <input
                        {...register("email", registerOptions.email)}
                        type="text"
                        className="form-control"
                        placeholder="Email (optional)"
                      />
                      {errors.email && (
                        <span className="text-danger small">
                          {errors.email.message}
                        </span>
                      )}
                    </div>

                    {/* Country code + Phone (required) */}
                    <div className="mb-3">
                      <Controller
                        name="countryPhone"
                        control={control}
                        rules={{
                          validate: (value) => {
                            const code = (value?.countryCode || "").trim();
                            const digits = String(value?.phone || "").replace(/\D/g, "");
                            if (!code) return "Country code is required";
                            if (!digits) return "Phone number is required";
                            if (digits.length !== 10)
                              return "Enter a valid 10-digit phone number";
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <CountryCode
                            label=""
                            placeholder="*Phone Number"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {errors.countryPhone && (
                        <span className="text-danger small">
                          {errors.countryPhone.message}
                        </span>
                      )}
                    </div>

                    <div className="mb-3">
                      <div className="password">
                        <input
                          {...register("password", registerOptions.password)}
                          type={passwordShow ? "text" : "password"}
                          className="form-control"
                          placeholder="*Password"
                        />
                        <span className="icon">
                          <i
                            className="fas fa-eye"
                            onClick={() => setPasswordShow(!passwordShow)}
                          ></i>
                        </span>
                        {errors.password && (
                          <span className="text-danger small">
                            {errors.password.message}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="password">
                        <input
                          {...register(
                            "confirmPassword",
                            registerOptions.confirmPassword
                          )}
                          type={passwordShow ? "text" : "password"}
                          className="form-control"
                          placeholder="*Confirm Password"
                        />
                        <span className="icon">
                          <i
                            className="fas fa-eye"
                            onClick={() => setPasswordShow(!passwordShow)}
                          ></i>
                        </span>
                        {errors.confirmPassword && (
                          <span className="text-danger small">
                            {errors.confirmPassword.message}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Global form error (register) */}
                    {!!formError && (
                      <div className="alert alert-danger" role="alert" style={{ fontSize: 14 }}>
                        {formError}
                      </div>
                    )}

                    <button
                      className="btn_secondary btn_md w-100 mt-5 text-center"
                      type="submit"
                      disabled={submitting}
                    >
                      {submitting ? "Signing Up..." : "Sign Up"}
                    </button>

                    <p className="already">
                      Already have an account? <Link to="/login">Sign In</Link>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
